<div
  class="app-shell"
  [class.app-shell--collapsed]="isCollapsed()"
  [class.app-shell--mobile-open]="mobileOpen()">

  @if (mobileOpen()) {
    <button type="button" class="shell-backdrop" (click)="closeMobile()" aria-label="Close menu"></button>
  }

  <!-- Single left sidebar -->
  <aside class="sidebar" aria-label="Main navigation">
    <div class="sidebar-brand">
      <div class="brand-mark" aria-hidden="true">
        <mat-icon>hub</mat-icon>
      </div>
      <div class="brand-copy">
        <strong>Trusted</strong>
        <span>Technologies</span>
      </div>
      <button
        mat-icon-button
        type="button"
        class="collapse-btn"
        (click)="toggleSidebar()"
        [attr.aria-label]="isCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
        [matTooltip]="isCollapsed() ? 'Expand' : 'Collapse'"
        matTooltipPosition="right">
        <mat-icon>{{ isCollapsed() ? 'chevron_right' : 'chevron_left' }}</mat-icon>
      </button>
    </div>

    @if (user()) {
      <div class="sidebar-user">
        <div class="avatar">{{ initials() }}</div>
        <div class="sidebar-user-meta">
          <strong>{{ displayName() }}</strong>
          <span>{{ rolesLabel() || 'User' }}</span>
        </div>
      </div>
    }

    <nav class="sidebar-nav" role="navigation">
      @for (section of navSections(); track section.id) {
        <div
          class="nav-section"
          [class.nav-section--open]="isSectionExpanded(section)"
          [class.nav-section--active]="sectionHasActive(section)">

          @if (section.flat && section.items.length === 1) {
            <a
              class="nav-section-btn nav-link"
              [routerLink]="section.items[0].route"
              routerLinkActive="nav-link--active"
              [routerLinkActiveOptions]="{ exact: true }"
              [matTooltip]="isCollapsed() ? section.label : ''"
              matTooltipPosition="right">
              <mat-icon>{{ section.icon }}</mat-icon>
              <span class="nav-label">{{ section.label }}</span>
            </a>
          } @else {
            <button
              type="button"
              class="nav-section-btn"
              [attr.aria-expanded]="isSectionExpanded(section)"
              [matTooltip]="isCollapsed() ? section.label : ''"
              matTooltipPosition="right"
              (click)="toggleSection(section, $event)">
              <mat-icon>{{ section.icon }}</mat-icon>
              <span class="nav-label">{{ section.label }}</span>
              <mat-icon class="chevron">expand_more</mat-icon>
            </button>

            <div
              class="nav-children"
              [class.nav-children--open]="isSectionExpanded(section)"
              [attr.aria-hidden]="!isSectionExpanded(section)">
              <div class="nav-children-inner">
                @for (item of section.items; track item.route + item.label) {
                  <a
                    class="nav-link nav-link--child"
                    [routerLink]="item.route"
                    routerLinkActive="nav-link--active"
                    [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
                    [matTooltip]="isCollapsed() ? item.label : ''"
                    matTooltipPosition="right">
                    <mat-icon>{{ item.icon }}</mat-icon>
                    <span class="nav-label">{{ item.label }}</span>
                  </a>
                }
              </div>
            </div>
          }
        </div>
      }
    </nav>

    <div class="sidebar-footer">
      <button
        type="button"
        class="nav-section-btn"
        (click)="prefs.cycleTheme()"
        [matTooltip]="'Theme: ' + themeLabel()"
        matTooltipPosition="right">
        <mat-icon>{{ themeIcon() }}</mat-icon>
        <span class="nav-label">{{ themeLabel() }} theme</span>
      </button>
    </div>
  </aside>

  <!-- Workspace -->
  <div class="workspace">
    <header class="app-header">
      <div class="header-left">
        @if (isMobile()) {
          <button mat-icon-button type="button" (click)="toggleSidebar()" aria-label="Open menu">
            <mat-icon>menu</mat-icon>
          </button>
        }
        <div class="header-titles">
          <app-breadcrumb [crumbs]="breadcrumbs()" />
          <h1 class="page-title">{{ pageTitle() }}</h1>
        </div>
      </div>

      <div class="header-search" [class.header-search--open]="searchFocused() || searchQuery()">
        <mat-icon class="search-icon" aria-hidden="true">search</mat-icon>
        <label class="sr-only" for="shell-search">Search</label>
        <input
          id="shell-search"
          type="search"
          placeholder="Search pages, issues… Ctrl+K"
          [ngModel]="searchQuery()"
          (ngModelChange)="onSearchInput($event)"
          (focus)="searchFocused.set(true)"
          (blur)="searchFocused.set(false)"
          autocomplete="off" />
        @if (searchFocused() || searchQuery()) {
          <div class="search-dropdown" role="listbox">
            @if (!searchQuery()) {
              @if (recentItems().length) {
                <div class="search-section-label">Recent</div>
                @for (item of recentItems(); track item.route) {
                  <button type="button" class="search-item" role="option" (mousedown)="goRecent(item)">
                    <mat-icon>history</mat-icon>
                    <span>{{ item.label }}</span>
                    <code>{{ item.route }}</code>
                  </button>
                }
              }
              @if (favoriteItems().length) {
                <div class="search-section-label">Favorites</div>
                @for (item of favoriteItems(); track item.route) {
                  <button type="button" class="search-item" role="option" (mousedown)="goSearchResult(item)">
                    <mat-icon>star</mat-icon>
                    <span>{{ item.label }}</span>
                    <code>{{ item.route }}</code>
                  </button>
                }
              }
              @if (!recentItems().length && !favoriteItems().length) {
                <div class="search-hint">Search pages and issues · Ctrl+K</div>
              }
            } @else {
              @if (searchResults().length) {
                <div class="search-section-label">Pages</div>
                @for (item of searchResults(); track item.route + item.label) {
                  <button type="button" class="search-item" role="option" (mousedown)="goSearchResult(item)">
                    <mat-icon>{{ item.icon }}</mat-icon>
                    <span>{{ item.label }}</span>
                    <code>{{ item.route }}</code>
                  </button>
                }
              }
              @if (issueSuggestions().length) {
                <div class="search-section-label">Issues</div>
                @for (hit of issueSuggestions(); track hit.id) {
                  <button type="button" class="search-item" role="option" (mousedown)="goIssueSuggestion(hit)">
                    <mat-icon>confirmation_number</mat-icon>
                    <span>{{ hit.key }} — {{ hit.summary || 'Issue' }}</span>
                  </button>
                }
              }
              @if (!searchResults().length && !issueSuggestions().length) {
                <div class="search-hint">No matches for “{{ searchQuery() }}”</div>
              }
            }
          </div>
        }
      </div>

      <div class="header-actions">
        <button
          mat-icon-button
          type="button"
          (click)="toggleCurrentFavorite()"
          [matTooltip]="isCurrentFavorite() ? 'Remove from favorites' : 'Favorite this page'"
          [attr.aria-label]="isCurrentFavorite() ? 'Remove from favorites' : 'Favorite this page'"
          [attr.aria-pressed]="isCurrentFavorite()">
          <mat-icon [class.favorited]="isCurrentFavorite()">{{ isCurrentFavorite() ? 'star' : 'star_border' }}</mat-icon>
        </button>

        <button mat-icon-button type="button" (click)="showShortcutsHelp()" matTooltip="Keyboard shortcuts (?)" aria-label="Keyboard shortcuts">
          <mat-icon>keyboard</mat-icon>
        </button>

        <button mat-icon-button type="button" [matMenuTriggerFor]="createMenu" matTooltip="Quick create" aria-label="Quick create">
          <mat-icon>add_circle</mat-icon>
        </button>
        <mat-menu #createMenu="matMenu">
          @for (action of quickCreate(); track action.route + action.label) {
            <button mat-menu-item type="button" [routerLink]="action.route">
              <mat-icon>{{ action.icon }}</mat-icon>
              <span>{{ action.label }}</span>
            </button>
          }
        </mat-menu>

        <button
          mat-icon-button
          type="button"
          routerLink="/notifications"
          matTooltip="Notifications"
          aria-label="Notifications"
          [matBadge]="unreadCount() || null"
          [matBadgeHidden]="!unreadCount()"
          matBadgeSize="small"
          matBadgeColor="warn">
          <mat-icon>notifications</mat-icon>
        </button>

        <button mat-icon-button type="button" routerLink="/chat" matTooltip="Messages" aria-label="Messages">
          <mat-icon>chat_bubble</mat-icon>
        </button>

        <button mat-icon-button type="button" (click)="prefs.cycleTheme()" [matTooltip]="'Theme: ' + themeLabel()" [attr.aria-label]="'Theme: ' + themeLabel()">
          <mat-icon>{{ themeIcon() }}</mat-icon>
        </button>

        <button mat-button type="button" class="profile-chip" [matMenuTriggerFor]="profileMenu">
          <div class="avatar avatar--sm">{{ initials() }}</div>
          <span class="profile-name">{{ displayName() }}</span>
          <mat-icon>expand_more</mat-icon>
        </button>
        <mat-menu #profileMenu="matMenu">
          <button mat-menu-item type="button" disabled>
            <mat-icon>badge</mat-icon>
            {{ rolesLabel() || 'No roles' }}
          </button>
          <mat-divider></mat-divider>
          <button mat-menu-item type="button" routerLink="/settings">
            <mat-icon>settings</mat-icon>
            Settings
          </button>
          <button mat-menu-item type="button" (click)="logout()">
            <mat-icon>logout</mat-icon>
            Logout
          </button>
        </mat-menu>
      </div>
    </header>

    <main class="workspace-content" id="main-content" tabindex="-1">
      <router-outlet></router-outlet>
    </main>
  </div>

  <app-issue-drawer />
</div>
